import type { MouseEvent } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { Icon, Separator, Tooltip } from '~/components';
import { Popover } from '~/components/Popover';
import { useArtistDetailsQuery } from '~/modules/artist-roster/hooks/useArtistDetailsQuery';
import type { AssetFamily } from '~/modules/artist-roster/types';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { GiveBenchmarkFeedback } from './components/GiveBenchmarkFeedback/GiveBenchmarkFeedback';

interface TrackTableLinkMenuProps {
  trackData: AssetFamily;
}

export function TrackTableLinkMenu({ trackData }: TrackTableLinkMenuProps) {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const { partyId } = useParams<{ partyId: string }>();
  const { data: artistData } = useArtistDetailsQuery({ partyId });
  const [searchParams] = useSearchParams();
  const [isArtistCollaboratorTracksEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgArtistCollaboratorTracks,
  ]);

  const isTableViewMetadata =
    isArtistCollaboratorTracksEnabled &&
    searchParams.get('table_view') === 'metadata';

  const handleLinkClick = ({
    event,
    linkName,
  }: {
    event: MouseEvent;
    linkName: string;
  }) => {
    event.stopPropagation();

    if (artistData) {
      trackEvent('artistTrackTableInteraction', {
        artist_id: artistData.partyId,
        artist_name: artistData.name,
        click_type: linkName,
        track_name: trackData.title,
        family_id: trackData.id,
      });
    }
  };

  // If there are no links and we are in "Track metadata" view, don't show the three-dot menu button.
  if (isTableViewMetadata && !trackData.links) {
    return null;
  }

  return (
    <Popover
      side="left"
      sideOffset={-5}
      content={
        <div className="flex flex-col rounded-sm border bg-level-04 border-emphasis-strong text-default">
          {trackData.links
            ? Object.entries(trackData.links).map(
                ([key, value], index, array) => {
                  const isPreRelease =
                    !trackData.initialReleaseDate ||
                    new Date(trackData.initialReleaseDate) >= new Date();

                  if (key !== 'airtable' && isPreRelease) {
                    return null;
                  }

                  return (
                    <Fragment key={key}>
                      <a
                        href={value}
                        target="_blank"
                        className="flex w-full items-center justify-between gap-xl px-md py-sm transition-colors first:rounded-t-sm last:rounded-b-sm hover:bg-emphasis-default"
                        rel="noopener noreferrer"
                        onClick={(event) =>
                          handleLinkClick({ event, linkName: key })
                        }
                      >
                        <span>{t(`trackList.header.${key}`)}</span>
                        <Icon type="external-link" variant="solid" size="sm" />
                      </a>
                      {index < array.length - 1 && !isPreRelease ? (
                        <Separator className="m-0 w-full" />
                      ) : null}
                    </Fragment>
                  );
                },
              )
            : null}
          {!isTableViewMetadata ? (
            <GiveBenchmarkFeedback
              artistData={artistData}
              trackData={trackData}
            />
          ) : null}
        </div>
      }
      stopPropagation
      triggerAsChild
    >
      <button
        aria-label={t('trackList.header.additionalLinks')}
        className="rounded-full border p-sm duration-300 ease-out border-transparent hover:bg-emphasis-subtle hover:border-emphasis-strong"
      >
        <Tooltip
          content={t('trackList.tooltips.additionalMenu')}
          delayDuration={0}
          sideOffset={16}
          side="bottom"
          align="end"
          size="md"
          triggerAsChild
        >
          <div className="flex size-md items-center justify-center">
            <Icon type="ellipsis-vertical" variant="regular" size="sm" />
          </div>
        </Tooltip>
      </button>
    </Popover>
  );
}

TrackTableLinkMenu.displayName = 'TrackTableLinkMenu';
